/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {RoadService} from './features/road.service';
import {SpatialUtilService} from './spatial-util.service';
import {Observable, of, tap} from 'rxjs';
import {NgEventBus} from 'ng-event-bus';
import {switchMap} from 'rxjs/operators';
import {LookupService} from '../../common/service/lookup.service';
import {RoadTypeLookup} from '../../common/models/road-type.model';
import {forkJoinDefaultIfEmpty} from '../../common/observable.utils';

@Injectable({
    providedIn: 'root'
})
export class RoadReconciliationService {
    title = 'RoadReconciliationService';

    constructor(
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private roadService: RoadService,
        private spatialUtilService: SpatialUtilService
    ) {}

    reconcileRoads(roads: any[], exemptedRoads: any[], isRoadWidening: boolean, isRoadLGA: boolean) {
        const logPrefix = `${this.title} - reconcileRoads`;
        console.debug(logPrefix);

        const roadsToBeAdded = [];
        const roadsToBeUpdated = [];
        const roadsToBeDeleted = [];

        const nonSpatialRoads = this.getNonSpatialRoads(roads, exemptedRoads, isRoadWidening, isRoadLGA);
        const spatialRoads: any[] = this.spatialUtilService.getRoads();
        const spatialRoadAttributesList: any[] = spatialRoads.map(v => structuredClone(v.attributes));

        const roadsMap = {};

        spatialRoadAttributesList.forEach(spatialRoadAttributes => {
            roadsMap[spatialRoadAttributes.roadid] = {};
            roadsMap[spatialRoadAttributes.roadid].spatialRoad = spatialRoadAttributes;
        });

        const spatialRoadUpdates = [];

        nonSpatialRoads.forEach(nonSpatialRoad => {
            roadsMap[nonSpatialRoad.roadid] = roadsMap[nonSpatialRoad.roadid] || {};
            roadsMap[nonSpatialRoad.roadid].nonSpatialRoad = nonSpatialRoad;

            // Upon snapshot restore after non-spatial roads deleted then re-added, any road feature associations should be restored
            if (!roadsMap[nonSpatialRoad.roadid].spatialRoad) {
                const spatialRoad = spatialRoads.find(
                    r =>
                        roadsMap[r.attributes.roadid] &&
                        !roadsMap[r.attributes.roadid].nonSpatialRoad &&
                        r.attributes.name === (nonSpatialRoad.name || '') &&
                        r.attributes.type === nonSpatialRoad.type &&
                        r.attributes.typecode === (nonSpatialRoad.typecode || '') &&
                        r.attributes.suffix === (nonSpatialRoad.suffix || '')
                );
                if (spatialRoad) {
                    const spatialRoadUpdate = this.spatialUtilService.copyGraphic(spatialRoad);
                    spatialRoadUpdate.attributes.roadid = nonSpatialRoad.roadid;
                    spatialRoadUpdates.push(spatialRoadUpdate);
                    delete roadsMap[spatialRoad.attributes.roadid];
                    delete roadsMap[nonSpatialRoad.roadid];
                }
            }
        });

        return (spatialRoadUpdates.length ? this.spatialUtilService.applyEdits('road', null, spatialRoadUpdates, null) : of(null)).pipe(
            switchMap(() => {
                Object.values(roadsMap).forEach((roadMapping: any) => {
                    if (roadMapping.spatialRoad && roadMapping.nonSpatialRoad) {
                        roadsToBeUpdated.push(roadMapping.nonSpatialRoad);
                    } else if (roadMapping.spatialRoad && !roadMapping.nonSpatialRoad) {
                        roadsToBeDeleted.push(roadMapping.spatialRoad);
                    } else if (roadMapping.nonSpatialRoad && !roadMapping.spatialRoad) {
                        roadsToBeAdded.push(roadMapping.nonSpatialRoad);
                    }
                });

                const observables: Observable<any>[] = [];

                if (roadsToBeDeleted.length) {
                    observables.push(this.roadService.deleteRoads(roadsToBeDeleted).pipe(tap(() => this.eventBus.cast('featuresUpdated'))));
                } else {
                    console.debug(`${logPrefix} - there are no roads to be deleted`);
                }

                if (roadsToBeAdded.length) {
                    observables.push(this.roadService.addRoad(roadsToBeAdded));
                } else {
                    console.debug(`${logPrefix} - there are no roads to be created`);
                }

                if (roadsToBeUpdated.length) {
                    observables.push(this.roadService.updateRoads(roadsToBeUpdated));
                } else {
                    console.debug(`${logPrefix} - there are no roads to be updated`);
                }

                return forkJoinDefaultIfEmpty(observables);
            })
        );
    }

    private getNonSpatialRoads(roads: any[], exemptedRoads: any[], isRoadWidening: boolean, isRoadLGA: boolean) {
        console.debug(`${this.title} - getNonSpatialRoads`);

        const nonSpatialRoads: any[] = [];

        roads.forEach(road => {
            if (road.id && road.road) {
                const roadDetails = road.road;
                nonSpatialRoads.push({
                    roadid: road.id,
                    type: 1,
                    name: roadDetails.roadName,
                    typecode: roadDetails.roadTypeDescription,
                    suffix: roadDetails.roadSuffixDescription
                });
            }
        });

        exemptedRoads.forEach(exemptedRoad => {
            if (exemptedRoad.id && exemptedRoad.name) {
                nonSpatialRoads.push({
                    roadid: exemptedRoad.id,
                    type: 2,
                    name: exemptedRoad.name
                });
            }
        });

        const roadTypeLookups: RoadTypeLookup[] = this.lookupService.getLookups('roadType');

        if (isRoadWidening) {
            const roadWideningRoadTypeLookup = roadTypeLookups.find(road => road.code === 'ROAD_WIDENING');
            nonSpatialRoads.push({
                roadid: 1000000000,
                type: roadWideningRoadTypeLookup.id,
                name: roadWideningRoadTypeLookup.defaultRoadName
            });
        }

        if (isRoadLGA) {
            const roadLGARoadTypeLookup = roadTypeLookups.find(road => road.code === 'ROAD_LGA');
            nonSpatialRoads.push({
                roadid: 1000000001,
                type: roadLGARoadTypeLookup.id,
                name: roadLGARoadTypeLookup.defaultRoadName
            });
        }

        return nonSpatialRoads;
    }
}
